import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  ALTERNATIVE_LOADING_STATUS,
  FAILURE_STATUS,
  IDLE_STATUS,
  LOADING_STATUS,
  PREPARING_ACTION_STATUS,
  SUCCESS_STATUS,
} from '../status';
import {
  fetchAll,
  fetchById,
  activateById,
  inactivateById,
} from './cotacaoMoedaAPI';
import {
  CONSULT_MODE,
  CREATE_MODE,
  UPDATE_MODE,
  NONE_MODE,
  NONE_BACKGROUND_MODE,
  BACKGROUND_CREATE_MODE,
} from '../mode';
import { IDLE_CONTROL } from '../config-control';
import i18n from '../../i18n';

const initialState = {
  status: IDLE_STATUS,
  mode: {
    main: NONE_MODE,
    background: NONE_BACKGROUND_MODE,
  },
  error: null,
  response: {
    status: null,
    message: null,
    data: null,
  },
  config: { control: IDLE_CONTROL },
  list: [],
  model: {
    id: '',
    description: '',
    code: '',
    active: null,
    data: '',
    cambio: 0,
    percentualMarkup: 0,
    moeda: {
      id: '',
    },

    insertionDate: null,
  },
};

const fetchAllAsync = createAsyncThunk(
  'cotacao/fetchAll',
  async () => {
    const { data } = await fetchAll();
    return { data };
  }
);

const fetchByIdAsync = createAsyncThunk(
  'cotacao/fetchById',
  async (data) => {
    const response = await fetchById(data);
    return {
      response: {
        status: response.status,
        data: response.data,
      },
    };
  }
);

const activateByIdAsync = createAsyncThunk(
  'cotacao/activateById',
  async (id) => {
    const { status, data } = await activateById(id);
    return { response: { status, data } };
  }
);

const inactivateByIdAsync = createAsyncThunk(
  'cotacao/inactivateById',
  async (id) => {
    const { status, data } = await inactivateById(id);
    return { response: { status, data } };
  }
);

const cotacaoMoedaSlice = createSlice({
  name: 'cotacaoMoeda',
  initialState,
  reducers: {
    changeStatusTo: (state, action) => {
      state.status = action.payload.status;
    },
    loading: (state) => {
      state.status = LOADING_STATUS;
    },
    success: (state) => {
      state.status = SUCCESS_STATUS;
    },
    failure: (state) => {
      state.status = FAILURE_STATUS;
    },
    preparingAction: (state) => {
      state.status = PREPARING_ACTION_STATUS;
    },
    changeToCreateMode: (state) => {
      state.mode.main = CREATE_MODE;
    },
    changeToConsultMode: (state) => {
      state.mode.main = CONSULT_MODE;
    },
    changeToUpdateMode: (state) => {
      state.mode.main = UPDATE_MODE;
    },
    resetMode: (state) => {
      state.mode.main = NONE_MODE;
    },
    changeToBackgroundCreateMode: (state) => {
      state.mode.background = BACKGROUND_CREATE_MODE;
    },
    resetBackgroundMode: (state) => {
      state.mode.background = NONE_BACKGROUND_MODE;
    },
    resetStatus: (state) => {
      state.status = IDLE_STATUS;
    },
    addToList: (state, action) => {
      state.list = [...state.list, action.payload.data];
    },
    updateOnList: (state, action) => {
      state.list = state.list.map((current) => {
        if (current.id === action.payload.data?.id) {
          return action.payload.data;
        }
        return current;
      });
    },
    setResponse: (state, action) => {
      state.response = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearResponse: (state) => {
      state.response = {
        status: null,
        message: null,
        data: null,
      };
    },
    resetModel: (state) => {
      state.model = initialState.model;
    },
    setModel: (state, action) => {
      state.model = {
        ...action.payload,
      };
    },
    setConfig: (state, action) => {
      state.config = action.payload;
    },
    changeControlTo: (state, action) => {
      state.config.control = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllAsync.pending, (state) => {
        state.status = ALTERNATIVE_LOADING_STATUS;
      })
      .addCase(fetchAllAsync.fulfilled, (state, action) => {
        state.status = IDLE_STATUS;
        state.list = action.payload.data;
      })
      .addCase(fetchAllAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroCarregarListaCotacoesMoeda', { erro: action.error.message }),
        };
      })
      .addCase(fetchByIdAsync.pending, (state) => {
        state.status = LOADING_STATUS;
      })
      .addCase(fetchByIdAsync.fulfilled, (state, action) => {
        if (action.payload.response.status === 200) {
          state.model = action.payload.response.data;
          state.status = SUCCESS_STATUS;
        }
      })
      .addCase(fetchByIdAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroCarregarCotacoesMoeda', { erro: action.error.message }),
        };
      })
      .addCase(activateByIdAsync.pending, (state) => {
        state.status = LOADING_STATUS;
      })
      .addCase(activateByIdAsync.fulfilled, (state, action) => {
        state.status = SUCCESS_STATUS;
        state.response.status = action.payload.response.status;
        state.response.data = action.payload.response.data;

        const { id } = action.payload.response.data;
        state.response.message = i18n.t('com.muralis.qcx.mensagem.cotacaoMoedaAtivada', { id });

        if (state.response.status === 200) {
          state.list = state.list
            .map((current) => (
              current.id === action.payload.response.data.id
                ? action.payload.response.data
                : current
            ));
        }
      })
      .addCase(activateByIdAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroAtivarCotacaoMoeda', { erro: action.error.message }),
        };
      })
      .addCase(inactivateByIdAsync.pending, (state) => {
        state.status = LOADING_STATUS;
      })
      .addCase(inactivateByIdAsync.fulfilled, (state, action) => {
        state.status = SUCCESS_STATUS;
        state.response.status = action.payload.response.status;
        state.response.data = action.payload.response.data;

        const { id } = action.payload.response.data;
        state.response.message = i18n.t('com.muralis.qcx.mensagem.cotacaoMoedaInativada', { id });

        if (state.response.status === 200) {
          state.list = state.list
            .map((current) => (
              current.id === action.payload.response.data.id
                ? action.payload.response.data
                : current
            ));
        }
      })
      .addCase(inactivateByIdAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroInativarCotacaoMoeda', { erro: action.error.message }),
        };
      });
  },
});

const {
  changeStatusTo,
  loading,
  success,
  failure,
  changeControlTo,
  changeToBackgroundCreateMode,
  preparingAction,
  resetBackgroundMode,
  resetMode,
  setConfig,
  changeToCreateMode,
  changeToConsultMode,
  changeToUpdateMode,
  resetStatus,
  addToList,
  updateOnList,
  setResponse,
  setResponseMessage,
  clearResponse,
  setError,
  resetModel,
  setModel,
} = cotacaoMoedaSlice.actions;

const selectCotacoesMoeda = (state) => state.cotacaoMoeda.list;
const selectStatus = (state) => state.cotacaoMoeda.status;
const selectError = (state) => state.cotacaoMoeda.error;
const selectResponse = (state) => state.cotacaoMoeda.response;
const selectCotacaoMoeda = (state) => state.cotacaoMoeda.model;
const selectMode = (state) => state.cotacaoMoeda.mode.main;
const selectBackgroundMode = (state) => state.cotacaoMoeda.mode.background;
const selectConfigControl = (state) => state.cotacaoMoeda.config.control;

export {
  cotacaoMoedaSlice,
  changeStatusTo,
  loading,
  success,
  failure,
  changeControlTo,
  changeToBackgroundCreateMode,
  preparingAction,
  resetBackgroundMode,
  resetMode,
  setConfig,
  changeToCreateMode,
  changeToConsultMode,
  changeToUpdateMode,
  resetStatus,
  addToList,
  updateOnList,
  setResponse,
  setResponseMessage,
  clearResponse,
  setError,
  resetModel,
  fetchAllAsync,
  fetchByIdAsync,
  activateByIdAsync,
  inactivateByIdAsync,
  setModel,
  selectCotacoesMoeda,
  selectCotacaoMoeda,
  selectStatus,
  selectError,
  selectResponse,
  selectMode,
  selectBackgroundMode,
  selectConfigControl,
};

export default cotacaoMoedaSlice.reducer;
